import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { v, FieldRow, Section } from '../../atoms/index.js';
export const CATEGORY_OPTIONS = [
    { value: 'halsa', label: 'Uppgifter om hälsa eller funktionsnedsättning', code: 'halsa', summary: 'Data on health or disability.' },
    { value: 'bostadsanpassning', label: 'Uppgifter om behov av bostadsanpassning', code: 'bostadsanpassning', summary: 'Data on the need for home adaptation.' },
    {
        value: 'vardgivare',
        label: 'Uppgifter från läkare, arbetsterapeut eller annan vårdgivare',
        code: 'vardgivare',
        summary: 'Data from a doctor, occupational therapist or other care provider.',
    },
    { value: 'kommun', label: 'Uppgifter från kommun eller socialtjänst', code: 'kommun', summary: 'Data from the municipality or social services.' },
    { value: 'annat', label: 'Annat', code: 'annat', summary: 'Another category of sensitive data, specified in free text.' },
];
/** Document sections 1–8, shared by both templates. The closing §9
 *  (Samtycke / Underskrift) differs and is appended by each template. */
export const SHARED_SECTION_DEFS = [
    { ref: 'parter', title: 'Parter' },
    { ref: 'bakgrund', title: 'Bakgrund' },
    { ref: 'uppgifter', title: 'Uppgifter som omfattas av samtycket' },
    { ref: 'andamal', title: 'Ändamål med behandlingen' },
    { ref: 'mottagare', title: 'Mottagare av uppgifterna' },
    { ref: 'lagringstid', title: 'Lagringstid' },
    { ref: 'frivillighet', title: 'Frivillighet och återkallelse' },
    { ref: 'rattigheter', title: 'Registrerades rättigheter' },
];
/**
 * Shared variable schema. The hyresvärd/hyresgäst identity + the avtalsnr
 * are sourced differently per variant:
 *   - `'bilaga'`     — `derivedFromParent` (overlaid from the parent lease).
 *   - `'standalone'` — autofilled from the `(lessor,0)` / `(lessee,0)` party.
 * The special-category checkboxes are authored on the samtycke in both.
 */
export function buildSharedFields(variant) {
    const party = (role, field) => ({ kind: 'party', role, ordinal: 0, field });
    // Identity field — derived from the parent lease on the bilaga,
    // autofilled from the party on the standalone.
    const ident = (role, partyField) => variant === 'standalone'
        ? { autofill: party(role, partyField) }
        : { derivedFromParent: true, help: 'Hämtas från hyresavtalet.' };
    return [
        {
            key: 'apartment_ref',
            label: 'Avtal / lägenhet nr',
            type: 'text',
            section: 'parter',
            requiredAtFinal: true,
            ...(variant === 'standalone'
                ? {}
                : {
                    derivedFromParent: true,
                    derivedFrom: 'contract_number',
                    help: 'Hämtas från hyresavtalet.',
                }),
        },
        // §1 Parter — Hyresvärd
        {
            key: 'landlord_name',
            label: 'Namn / Firma',
            type: 'text',
            section: 'parter',
            requiredAtFinal: true,
            ...ident('lessor', 'legal_name'),
        },
        {
            key: 'landlord_org_number',
            label: 'Organisationsnummer',
            type: 'text',
            section: 'parter',
            requiredAtFinal: true,
            ...ident('lessor', 'id_number'),
        },
        {
            key: 'landlord_address',
            label: 'Adress (hyresvärd)',
            type: 'textarea',
            section: 'parter',
            ...ident('lessor', 'composed_address'),
        },
        // §1 Parter — Hyresgäst
        {
            key: 'tenant_name',
            label: 'Namn (hyresgäst)',
            type: 'text',
            section: 'parter',
            requiredAtFinal: true,
            ...ident('lessee', 'legal_name'),
        },
        {
            key: 'tenant_personnummer',
            label: 'Personnummer',
            type: 'text',
            section: 'parter',
            requiredAtFinal: true,
            help: variant === 'standalone'
                ? 'PII — ej sökbart.'
                : 'Hämtas från hyresavtalet. PII — ej sökbart.',
            ...(variant === 'standalone'
                ? { autofill: party('lessee', 'id_number') }
                : { derivedFromParent: true }),
        },
        {
            key: 'tenant_address',
            label: 'Adress (hyresgäst)',
            type: 'textarea',
            section: 'parter',
            // bostadshyra has no separate tenant address → plain on the bilaga.
            ...(variant === 'standalone'
                ? { autofill: party('lessee', 'composed_address') }
                : {}),
        },
        // §3 Uppgifter som omfattas av samtycket (authored here, both variants)
        {
            key: 'categories',
            label: 'Uppgifter som omfattas av samtycket',
            type: 'multi_select',
            section: 'uppgifter',
            requiredAtFinal: true,
            options: CATEGORY_OPTIONS.map((o) => ({
                value: o.value,
                label: o.label,
                code: o.code,
                summary: o.summary,
            })),
        },
        {
            key: 'categories_other',
            label: 'Annat (specificera)',
            type: 'text',
            section: 'uppgifter',
            visibleWhen: { categories: 'annat' },
            requiredWhen: { categories: 'annat' },
        },
    ];
}
export const SHARED_SAMPLE_VALUES = {
    apartment_ref: 'A-2042 / lgh 1101',
    landlord_name: 'Bostadsägarna Stockholm AB',
    landlord_org_number: '556789-1234',
    landlord_address: 'Drottninggatan 100\n111 60 Stockholm',
    tenant_name: 'Anna Andersson',
    tenant_personnummer: '19850412-1234',
    tenant_address: 'Götgatan 35\n116 21 Stockholm',
    categories: ['halsa', 'bostadsanpassning'],
    categories_other: '',
};
function BulletList({ items }) {
    return (_jsx("ul", { className: "mt-1 list-disc pl-5 text-[10.5px] leading-relaxed text-stone-700", children: items.map((it) => (_jsx("li", { children: it }, it))) }));
}
function Checkbox({ checked }) {
    return (_jsx("span", { className: "mr-1 inline-block h-3 w-3 border border-stone-500 text-center align-middle text-[9px] leading-3", "aria-hidden": true, children: checked ? '✓' : '' }));
}
/**
 * Sections 1–8 of the samtycke, rendered inside the caller's
 * `<SectionNumberingProvider>`. The caller renders the avtalsref line
 * above and appends its own §9 (Samtycke / Underskrift). Static clause
 * text is verbatim from the approved Word source.
 */
export function SamtyckeSharedBody({ values, mode, }) {
    const cats = Array.isArray(values.categories) ? values.categories : [];
    const has = (k) => mode === 'blank' || cats.includes(k);
    return (_jsxs(_Fragment, { children: [_jsxs("p", { className: "mb-3 text-[10.5px] text-stone-700", children: ["Bilaga till hyresavtal avseende l\u00E4genhet/lokal nr:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: v(values.apartment_ref) })] }), _jsxs(Section, { sectionRef: "parter", title: "Parter", children: [_jsx("p", { className: "mb-1 text-[10.5px] font-semibold", children: "Hyresv\u00E4rd" }), _jsx(FieldRow, { label: "Namn / Firma", children: v(values.landlord_name) }), _jsx(FieldRow, { label: "Organisationsnummer", children: v(values.landlord_org_number) }), _jsx(FieldRow, { label: "Adress", children: v(values.landlord_address) }), _jsx("p", { className: "mb-1 mt-3 text-[10.5px] font-semibold", children: "Hyresg\u00E4st" }), _jsx(FieldRow, { label: "Namn", children: v(values.tenant_name) }), _jsx(FieldRow, { label: "Personnummer", children: v(values.tenant_personnummer) }), _jsx(FieldRow, { label: "Adress", children: v(values.tenant_address) })] }), _jsx(Section, { sectionRef: "bakgrund", title: "Bakgrund", children: _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten har l\u00E4mnat eller kommer att l\u00E4mna uppgifter som omfattas av artikel 9 i Europaparlamentets och r\u00E5dets f\u00F6rordning (EU) 2016/679 (GDPR) och som \u00E4r n\u00F6dv\u00E4ndiga f\u00F6r att hyresv\u00E4rden ska kunna bed\u00F6ma, administrera eller tillgodose behov kopplade till bostaden eller hyresf\u00F6rh\u00E5llandet." }) }), _jsxs(Section, { sectionRef: "uppgifter", title: "Uppgifter som omfattas av samtycket", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Samtycket omfattar f\u00F6ljande uppgifter:" }), _jsx("div", { className: "mt-1 flex flex-col gap-1 text-[10.5px] leading-relaxed text-stone-700", children: CATEGORY_OPTIONS.map((o) => (_jsxs("div", { children: [_jsx(Checkbox, { checked: has(o.value) }), o.value === 'annat' ? (_jsxs(_Fragment, { children: ["Annat:", ' ', _jsx("span", { className: "border-b border-stone-300 font-serif", children: has('annat') ? v(values.categories_other) : '' })] })) : (o.label)] }, o.value))) })] }), _jsxs(Section, { sectionRef: "andamal", title: "\u00C4ndam\u00E5l med behandlingen", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Personuppgifterna f\u00E5r behandlas f\u00F6r att:" }), _jsx(BulletList, { items: [
                            'bedöma behov av anpassad bostad,',
                            'genomföra bostadsanpassningar,',
                            'administrera särskilda boendebehov,',
                            'fullgöra hyresvärdens skyldigheter enligt lag,',
                            'hantera ärenden som hyresgästen själv initierat,',
                            'fastställa, göra gällande eller försvara rättsliga anspråk.',
                        ] })] }), _jsxs(Section, { sectionRef: "mottagare", title: "Mottagare av uppgifterna", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Personuppgifterna f\u00E5r l\u00E4mnas till:" }), _jsx(BulletList, { items: [
                            'fastighetsförvaltare,',
                            'tekniska konsulter och entreprenörer,',
                            'kommun eller myndighet när det krävs för ändamålet,',
                            'personuppgiftsbiträden som behandlar uppgifter för hyresvärdens räkning.',
                        ] })] }), _jsx(Section, { sectionRef: "lagringstid", title: "Lagringstid", children: _jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Uppgifterna behandlas endast s\u00E5 l\u00E4nge de beh\u00F6vs f\u00F6r de \u00E4ndam\u00E5l som anges ovan eller s\u00E5 l\u00E4nge lag kr\u00E4ver." }) }), _jsxs(Section, { sectionRef: "frivillighet", title: "Frivillighet och \u00E5terkallelse", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Samtycket \u00E4r frivilligt." }), _jsx("p", { className: "mt-1 text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten har r\u00E4tt att n\u00E4r som helst \u00E5terkalla sitt samtycke genom skriftligt meddelande till hyresv\u00E4rden." }), _jsx("p", { className: "mt-1 text-[10.5px] leading-relaxed text-stone-700", children: "Ett \u00E5terkallande p\u00E5verkar inte lagligheten av behandling som skett innan samtycket \u00E5terkallades." })] }), _jsxs(Section, { sectionRef: "rattigheter", title: "Registrerades r\u00E4ttigheter", children: [_jsx("p", { className: "text-[10.5px] leading-relaxed text-stone-700", children: "Hyresg\u00E4sten har r\u00E4tt att:" }), _jsx(BulletList, { items: [
                            'begära information om behandlingen,',
                            'begära registerutdrag,',
                            'begära rättelse,',
                            'begära radering när lagens förutsättningar är uppfyllda,',
                            'begära begränsning av behandling,',
                            'inge klagomål till Integritetsskyddsmyndigheten (IMY).',
                        ] })] })] }));
}
/**
 * §9 Samtycke — the explicit consent sentence, shared by both variants.
 * On the bilaga it stands alone (signing rides the parent); on the
 * standalone the Underskrift block follows it.
 */
export const SAMTYCKE_TEXT = 'Jag samtycker härmed uttryckligen till att hyresvärden behandlar ovan angivna särskilda kategorier av personuppgifter för de ändamål som anges i denna bilaga.';
/** Shared description (the Word source's first paragraph — when-to-use). */
export const SAMTYCKE_DESCRIPTION = 'Hyresgästens uttryckliga samtycke (GDPR artikel 9) till att hyresvärden behandlar särskilda kategorier av personuppgifter (känsliga personuppgifter), exempelvis uppgifter om hälsa, funktionsnedsättning eller behov av bostadsanpassning.\n\n' +
    'Används endast när sådan känslig behandling är nödvändig och inte redan följer av lag eller annan tillämplig rättslig grund. För vanlig administration av hyresförhållandet (namn, personnummer, kontakt- och betalningsuppgifter) används i stället Personuppgiftsbilagan till hyresavtalet.';
//# sourceMappingURL=shared.js.map